/**
 * DataTable Component
 *
 * A table component with:
 * - Client-side pagination and sorting
 * - Configurable columns with custom cell renderers
 * - Collapsible filters panel in table header
 * - Download CSV functionality
 * - Actions column support
 */

import { type ReactNode, useState, useMemo } from 'react'
import { ArrowUpDown, ArrowUp, ArrowDown, Download, Filter } from 'lucide-react'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './table'
import { Card, CardContent } from './card'
import { Button } from './button'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './select'
import { cn, downloadAsCSV } from '@/lib/utils'
import {Input} from "@/components/ui/input.tsx";

export interface Column<T> {
  header: string | ReactNode
  accessorKey: keyof T
  sortField?: string
  sortable?: boolean
  cell?: (value: unknown, row: T) => ReactNode
  width?: {
    min?: number
    max?: number
    width?: number
  }
}

export interface SelectOption {
  value: string
  label: string
}

export interface FilterConfig {
  type: 'select' | 'text' | 'date' | 'number'
  key: string
  label: string
  options?: SelectOption[]
  placeholder?: string
  noFilterValue?: string // Value that means "no filter" (e.g., 'all')
}

interface DataTableProps<T> {
  columns: Column<T>[]
  data: T[]
  onRowClick?: (row: T) => void
  actions?: (row: T) => ReactNode
  showDownload?: boolean
  downloadFileName?: string
  selectedId?: string | number | null
  className?: string
  pageSize?: number
  defaultSort?: {
    field: string
    direction: 'asc' | 'desc'
  }
  // Filters support
  filters?: FilterConfig[]
  filterValues?: Record<string, string>
  onFilterChange?: (key: string, value: string) => void
}

export function DataTable<T>({
  columns,
  data,
  onRowClick,
  actions,
  showDownload = true,
  downloadFileName = 'data.csv',
  selectedId,
  className,
  pageSize = 50,
  defaultSort,
  filters,
  filterValues,
  onFilterChange,
}: DataTableProps<T>) {
  const [currentPage, setCurrentPage] = useState(0)
  const [sortField, setSortField] = useState<string>(defaultSort?.field || '')
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>(defaultSort?.direction || 'desc')
  const [showFiltersPanel, setShowFiltersPanel] = useState(false)

  // Sort data
  const sortedData = useMemo(() => {
    if (!sortField) return data

    return [...data].sort((a, b) => {
      const aVal = (a as Record<string, unknown>)[sortField]
      const bVal = (b as Record<string, unknown>)[sortField]

      if (aVal === null || aVal === undefined) return sortDirection === 'asc' ? -1 : 1
      if (bVal === null || bVal === undefined) return sortDirection === 'asc' ? 1 : -1

      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return sortDirection === 'asc' ? aVal - bVal : bVal - aVal
      }

      if (typeof aVal === 'string' && typeof bVal === 'string') {
        return sortDirection === 'asc'
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal)
      }

      return 0
    })
  }, [data, sortField, sortDirection])

  // Paginate data
  const paginatedData = useMemo(() => {
    const start = currentPage * pageSize
    return sortedData.slice(start, start + pageSize)
  }, [sortedData, currentPage, pageSize])

  const totalPages = Math.ceil(data.length / pageSize)

  const handleSort = (field: string) => {
    const newDirection: 'asc' | 'desc' = field === sortField && sortDirection === 'asc' ? 'desc' : 'asc'
    setSortField(field)
    setSortDirection(newDirection)
    setCurrentPage(0)
  }

  const getSortIcon = (field: string) => {
    if (field !== sortField) {
      return <ArrowUpDown className="w-3.5 h-3.5 ml-1 opacity-50" />
    }
    return sortDirection === 'asc' ? (
      <ArrowUp className="w-3.5 h-3.5 ml-1" />
    ) : (
      <ArrowDown className="w-3.5 h-3.5 ml-1" />
    )
  }

  const handleDownload = () => {
    const csvData = sortedData.map((row) => {
      const rowData: Record<string, unknown> = {}
      columns.forEach((col) => {
        const accessorKey = String(col.accessorKey)
        const value = (row as Record<string, unknown>)[col.accessorKey as string]
        if (col.cell && typeof col.cell === 'function') {
          const formatted = col.cell(value, row)
          rowData[accessorKey] = formatted && typeof formatted === 'object' ? value : formatted
        } else {
          rowData[accessorKey] = value
        }
      })
      return rowData
    })

    downloadAsCSV(
      csvData,
      columns.map((col) => ({
        header: typeof col.header === 'string' ? col.header : String(col.accessorKey),
        accessorKey: String(col.accessorKey),
      })),
      downloadFileName
    )
  }

  // Render filter
  const renderFilter = (filter: FilterConfig) => {
    const value = filterValues?.[filter.key] || filter.noFilterValue || 'all';

    switch (filter.type) {
      case 'select':
        return (
          <Select
            value={value}
            onValueChange={(val) => onFilterChange?.(filter.key, val)}
          >
            <SelectTrigger className="w-[140px] h-7">
              <SelectValue placeholder={filter.placeholder || filter.label} />
            </SelectTrigger>
            <SelectContent>
              {filter.options?.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        );

      case 'text':
        return (
          <Input
            value={value}
            onChange={(e) => onFilterChange?.(filter.key, e.target.value)}
            placeholder={filter.placeholder || filter.label}
            className="w-[160px] h-7"
          />
        );

      case 'date':
        return (
          <Input
            type="date"
            value={value}
            onChange={(e) => onFilterChange?.(filter.key, e.target.value)}
            placeholder={filter.placeholder || filter.label}
            className="w-[140px] h-7"
          />
        );

      case 'number':
        return (
          <Input
            type="number"
            value={value}
            onChange={(e) => onFilterChange?.(filter.key, e.target.value)}
            placeholder={filter.placeholder || filter.label}
            className="w-[140px] h-7"
          />
        );

      default:
        return null;
    }
  }

  // Check if any filter is active (not default value)
  const hasActiveFilters = useMemo(() => {
    if (!filters || !filterValues) return false
    return filters.some((filter) => {
      const value = filterValues[filter.key]
      return value && value !== (filter.noFilterValue || 'all')
    })
  }, [filters, filterValues])

  return (
    <Card className={cn('shadow-sm bg-white', className)}>
      <CardContent className="p-0">
        {/* Toolbar */}
        <div className="p-2 border-b">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {filters && filters.length > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowFiltersPanel(!showFiltersPanel)}
                  className={cn('h-8 gap-1.5', showFiltersPanel && 'bg-gray-100')}
                >
                  <Filter className="w-3.5 h-3.5" />
                  <span>Filters</span>
                  {hasActiveFilters && (
                    <span className="ml-1 w-2 h-2 rounded-full bg-blue-500" />
                  )}
                </Button>
              )}
              <span className="text-detail text-gray-500">
                {data.length} {data.length === 1 ? 'row' : 'rows'}
              </span>
            </div>

            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage((p) => Math.max(0, p - 1))}
                disabled={currentPage === 0}
                className="h-8"
              >
                Previous
              </Button>
              <span className="text-detail text-muted-foreground">
                Page {currentPage + 1} of {totalPages || 1}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage((p) => Math.min(totalPages - 1, p + 1))}
                disabled={currentPage >= totalPages - 1}
                className="h-8"
              >
                Next
              </Button>
              {showDownload && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleDownload}
                  className="h-8 w-8 p-0"
                  title="Download CSV"
                >
                  <Download className="w-4 h-4" />
                </Button>
              )}
            </div>
          </div>

          {/* Filters Panel */}
          {showFiltersPanel && filters && filters.length > 0 && (
            <div className="mt-2 p-4 border rounded-md bg-gray-50">
              <div className="flex flex-wrap gap-4">
                {filters.map((filter) => (
                  <div key={filter.key} className="flex flex-col gap-1">
                    <label className="font-medium">{filter.label}</label>
                    {renderFilter(filter)}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                {columns.map((column) => {
                  const isSortable = column.sortable !== false
                  const sortFieldToUse = column.sortField || String(column.accessorKey)

                  return (
                    <TableHead
                      key={String(column.accessorKey)}
                      onClick={isSortable ? () => handleSort(sortFieldToUse) : undefined}
                      className={cn(
                        'select-none',
                        isSortable && 'cursor-pointer hover:bg-gray-50',
                        !isSortable && 'cursor-default'
                      )}
                      style={{
                        minWidth: column.width?.min ? `${column.width.min}px` : undefined,
                        maxWidth: column.width?.max ? `${column.width.max}px` : undefined,
                        width: column.width?.width ? `${column.width.width}px` : undefined,
                      }}
                    >
                      <div className="flex items-center justify-between">
                        <span>{column.header}</span>
                        {isSortable && getSortIcon(sortFieldToUse)}
                      </div>
                    </TableHead>
                  )
                })}
                {actions && <TableHead className="text-right">Actions</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {paginatedData.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={columns.length + (actions ? 1 : 0)}
                    className="text-center py-8 text-gray-500"
                  >
                    No data
                  </TableCell>
                </TableRow>
              ) : (
                paginatedData.map((row, rowIndex) => (
                  <TableRow
                    key={rowIndex}
                    className={cn(
                      'hover:bg-gray-50',
                      onRowClick && !actions && 'cursor-pointer',
                      selectedId &&
                        (row as Record<string, unknown>).id === selectedId &&
                        'bg-green-100 border-l-[3px] border-l-green-700 hover:bg-green-100'
                    )}
                    onClick={() => !actions && onRowClick?.(row)}
                  >
                    {columns.map((column) => (
                      <TableCell
                        key={String(column.accessorKey)}
                        className="py-1"
                      >
                        {column.cell
                          ? column.cell((row as Record<string, unknown>)[column.accessorKey as string], row)
                          : String((row as Record<string, unknown>)[column.accessorKey as string] || '')}
                      </TableCell>
                    ))}
                    {actions && (
                      <TableCell className="py-1 text-right">
                        {actions(row)}
                      </TableCell>
                    )}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  )
}